import * as ModelEvent from "@node-elion/syncron";
import { compact } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import stringToUndefined from "../../utils/stringToUndefined";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { ModelId } from "../../types/ModelId";
import { Base, Language as Lang } from "..";

const logger = createLogger({ name: "PaymentSystem" });

const stringifyError = (err: unknown): string => {
	if (err instanceof Error) {
		return err.message; //  `${err.name}: ${err.message}`
	}
	try {
		return JSON.stringify(err);
	} catch {
		return String(err); // fallback на toString()
	}
};

class PaymentSystem extends Base {
	private static optionsToRequest(optns: PaymentSystem.SubscribeOptions) {
		return {
			lang: optns.language,
			limit: optns.limit,
			offset: optns.offset,
			types: optns.types,
		};
	}

	public static fromResponseMaid(
		data: any,
	): PaymentSystem.MaibPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.MaibPaymentConfiguration;

		const isExecutorAndMaib =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.MAIB;

		const payload: PaymentSystem.MaibPaymentConfiguration = {
			maibPaymentsKey: "",
			maibPaymentsSecretKey: "",
			maibSignatureId: "",

			maibPaymentsCurrency: MaibPaymentCurrency.MDL,
			maibPaymentsCountryIso: MaibPaymentCountryIso.MDA,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			maibCheck: false,
			maibAction: false,

			maibWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,
		};

		if (config?.maibCheck || isExecutorAndMaib) {
			payload.maibPaymentsKey = config.maibPaymentsKey;
			payload.maibPaymentsSecretKey = config.maibPaymentsSecretKey;
			payload.maibSignatureId = config.maibSignatureId;

			payload.maibPaymentsCurrency = config.maibPaymentsCurrency;
			payload.maibPaymentsCountryIso = config.maibPaymentsCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.maibCheck = config.maibCheck;
			payload.maibAction = config.maibAction;
		}

		return payload;
	}

	public static fromResponseAsset(
		data: any,
	): PaymentSystem.AssetPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.AssetPaymentConfiguration;

		const isExecutorAndAsset =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.ASSETPAYMENT;

		const payload: PaymentSystem.AssetPaymentConfiguration = {
			assetPaymentsFeePercentage: 0,
			assetPaymentsAdditionalFee: 0,
			assetPaymentsGlobalFeeFromSystem: false,
			assetPaymentsProcessingId: 0,
			assetPaymentsCurrency: PaymentCurrency.UAH,
			assetPaymentsCountryIso: PaymentCountryIso.UKR,
			assetPaymentsKey: "",
			assetPaymentsSecretKey: "",

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			assetCheck: false,
			assetAction: false,
			assetAllowQrPayment: false,
			assetAllowSmsPaymentLink: false,
		};

		if (config?.assetCheck || isExecutorAndAsset) {
			payload.assetPaymentsGlobalFeeFromSystem =
				config.assetPaymentsGlobalFeeFromSystem;
			payload.assetPaymentsFeePercentage =
				config.assetPaymentsFeePercentage;
			payload.assetPaymentsAdditionalFee =
				config.assetPaymentsAdditionalFee;

			payload.assetPaymentsProcessingId =
				config.assetPaymentsProcessingId;

			payload.assetPaymentsCurrency = config.assetPaymentsCurrency;
			payload.assetPaymentsCountryIso = config.assetPaymentsCountryIso;
			payload.assetPaymentsKey = config.assetPaymentsKey;
			payload.assetPaymentsSecretKey = config.assetPaymentsSecretKey;
			payload.assetAllowQrPayment = config.assetAllowQrPayment ?? false;
			payload.assetAllowSmsPaymentLink =
				config.assetAllowSmsPaymentLink ?? false;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.assetCheck = config.assetCheck;
			payload.assetAction = config.assetAction;
		}

		return payload;
	}

	public static fromResponseDinar(
		data: any,
	): PaymentSystem.DinarPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.DinarPaymentConfiguration;

		const isExecutorAndDinar =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.DINARPAY;

		const payload: PaymentSystem.DinarPaymentConfiguration = {
			dinarPayUsername: "",
			dinarPayPassword: "",
			dinarPayApiUrl: "",
			dinarPayCertificate: "",
			dinarPayCertPassword: "",

			dinarPayFeePercentage: 0,
			dinarPayAdditionalFee: 0,
			dinarPayGlobalFeeFromSystem: true,

			dinarPayCurrency: PaymentCurrency.AZN,
			dinarPayCountryIso: PaymentCountryIso.AZE,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			dinarPayCheck: false,
			dinarPayAction: false,

			dinarPayWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,
		};

		if (config?.dinarPayApiUrl) {
			payload.dinarPayApiUrl = config.dinarPayApiUrl;
		}
		if (config?.dinarPayCertificate) {
			payload.dinarPayCertificate = config.dinarPayCertificate;
		}
		if (config?.dinarPayCertPassword) {
			payload.dinarPayCertPassword = config.dinarPayCertPassword;
		}
		if (config?.dinarPayCheck || isExecutorAndDinar) {
			payload.dinarPayUsername = config.dinarPayUsername;
			payload.dinarPayPassword = config.dinarPayPassword;
			payload.dinarPayFeePercentage = config.dinarPayFeePercentage;
			payload.dinarPayAdditionalFee = config.dinarPayAdditionalFee;
			payload.dinarPayGlobalFeeFromSystem =
				config.dinarPayGlobalFeeFromSystem;

			payload.dinarPayCurrency = config.dinarPayCurrency;
			payload.dinarPayCountryIso = config.dinarPayCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.dinarPayCheck = config.dinarPayCheck;
			payload.dinarPayAction = config.dinarPayAction;
		}

		return payload;
	}

	public static fromResponseBog(
		data: any,
	): PaymentSystem.BogPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.BogPaymentConfiguration;

		const isExecutorAndBog =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.BOG;

		const payload: PaymentSystem.BogPaymentConfiguration = {
			bogClientId: "",
			bogClientSecret: "",
			bogApiUrl: "",

			bogFeePercentage: 0,
			bogAdditionalFee: 0,
			bogGlobalFeeFromSystem: true,

			bogCurrency: PaymentCurrency.GEL,
			bogCountryIso: PaymentCountryIso.GEO,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			bogCheck: false,
			bogAction: false,

			bogWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,
		};

		if (config?.bogApiUrl) {
			payload.bogApiUrl = config.bogApiUrl;
		}

		if (config?.bogCheck || isExecutorAndBog) {
			payload.bogClientId = config.bogClientId;
			payload.bogClientSecret = config.bogClientSecret;

			payload.bogFeePercentage = config.bogFeePercentage;
			payload.bogAdditionalFee = config.bogAdditionalFee;
			payload.bogGlobalFeeFromSystem = config.bogGlobalFeeFromSystem;

			payload.bogCurrency = config.bogCurrency;
			payload.bogCountryIso = config.bogCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.bogCheck = config.bogCheck;
			payload.bogAction = config.bogAction;
		}

		return payload;
	}

	public static fromResponseCybersource(
		data: any,
	): PaymentSystem.CybersourcePaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config =
			configuration as PaymentSystem.CybersourcePaymentConfiguration;

		const isExecutorAndCybersource =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.CYBERSOURCE;

		const payload: PaymentSystem.CybersourcePaymentConfiguration = {
			cybersourceMerchantId: "",
			cybersourceApiKeyId: "",
			cybersourceApiSecret: "",
			cybersourceApiUrl: "",
			cybersourceOrganizationId: "",

			cybersourceFeePercentage: 0,
			cybersourceAdditionalFee: 0,
			cybersourceGlobalFeeFromSystem: true,

			cybersourceCurrency: PaymentCurrency.MDL,
			cybersourceCountryIso: PaymentCountryIso.MDA,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			cybersourceCheck: false,
			cybersourceAction: false,

			cybersourceWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,

			cybersourceCreateTripReports: true,
			cybersourceMerchantLegalName: "",
			cybersourceMerchantWebsite: "",
			cybersourceSupportContacts: "",
		};

		if (config?.cybersourceApiUrl) {
			payload.cybersourceApiUrl = config.cybersourceApiUrl;
		}

		if (config) {
			payload.cybersourceCreateTripReports =
				config.cybersourceCreateTripReports ?? true;
			payload.cybersourceMerchantLegalName =
				config.cybersourceMerchantLegalName ?? "";
			payload.cybersourceMerchantWebsite =
				config.cybersourceMerchantWebsite ?? "";
			payload.cybersourceSupportContacts =
				config.cybersourceSupportContacts ?? "";
		}

		if (config?.cybersourceCheck || isExecutorAndCybersource) {
			payload.cybersourceMerchantId = config.cybersourceMerchantId;
			payload.cybersourceApiKeyId = config.cybersourceApiKeyId;
			payload.cybersourceApiSecret = config.cybersourceApiSecret;
			payload.cybersourceOrganizationId =
				config.cybersourceOrganizationId ?? "";

			payload.cybersourceFeePercentage = config.cybersourceFeePercentage;
			payload.cybersourceAdditionalFee = config.cybersourceAdditionalFee;
			payload.cybersourceGlobalFeeFromSystem =
				config.cybersourceGlobalFeeFromSystem;

			payload.cybersourceCurrency = config.cybersourceCurrency;
			payload.cybersourceCountryIso = config.cybersourceCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.cybersourceCheck = config.cybersourceCheck;
			payload.cybersourceAction = config.cybersourceAction;
		}

		return payload;
	}

	public static fromResponsePlaton(
		data: any,
	): PaymentSystem.PlatonPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config =
			configuration as PaymentSystem.PlatonPaymentConfiguration;

		const isExecutorAndPlaton =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.PLATON;

		const payload: PaymentSystem.PlatonPaymentConfiguration = {
			platonClientKey: "",
			platonClientPassword: "",
			platonApiUrl: "https://secure.platononline.com",

			platonFeePercentage: 0,
			platonAdditionalFee: 0,
			platonGlobalFeeFromSystem: true,

			platonCurrency: PaymentCurrency.UAH,
			platonCountryIso: PaymentCountryIso.UKR,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			platonCheck: false,
			platonAction: false,

			platonWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,

			platonPayoutToExecutorCardEnabled: false,
			platonAllowGooglePay: false,
			platonAllowApplePay: false,
		};

		if (config?.platonApiUrl) {
			payload.platonApiUrl = config.platonApiUrl;
		}

		if (config?.platonCheck || isExecutorAndPlaton) {
			payload.platonClientKey = config.platonClientKey;
			payload.platonClientPassword = config.platonClientPassword;

			payload.platonFeePercentage = config.platonFeePercentage;
			payload.platonAdditionalFee = config.platonAdditionalFee;
			payload.platonGlobalFeeFromSystem =
				config.platonGlobalFeeFromSystem;

			payload.platonCurrency = config.platonCurrency;
			payload.platonCountryIso = config.platonCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.platonCheck = config.platonCheck;
			payload.platonAction = config.platonAction;

			payload.platonPayoutToExecutorCardEnabled =
				config.platonPayoutToExecutorCardEnabled ?? false;
			payload.platonAllowGooglePay =
				config.platonAllowGooglePay ?? false;
			payload.platonAllowApplePay =
				config.platonAllowApplePay ?? false;
		}

		return payload;
	}

	public static fromResponseMonobank(
		data: any,
	): PaymentSystem.MonobankPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config =
			configuration as PaymentSystem.MonobankPaymentConfiguration;

		const isExecutorAndMonobank =
			config &&
			data.type === PaymentSystemFrontEndType.EXECUTOR &&
			paymentProvider === PaymentProvider.MONOBANK;

		const payload: PaymentSystem.MonobankPaymentConfiguration = {
			monobankToken: "",
			monobankApiUrl: "https://api.monobank.ua",

			monobankFeePercentage: 0,
			monobankAdditionalFee: 0,
			monobankGlobalFeeFromSystem: true,

			monobankCurrency: PaymentCurrency.UAH,
			monobankCountryIso: PaymentCountryIso.UKR,

			systemReturnFeeToExecutor: false,
			systemFeePercentageForExecutor: 0,
			systemFeeAdditionalForExecutor: 0,

			monobankCheck: false,
			monobankAction: false,

			monobankWithdrawMethod: WithdrawMethod.WITHDRAW_WITH_HOLD,

			monobankAllowGooglePay: false,
			monobankAllowApplePay: false,
			monobankAllowQrPayment: false,
			monobankAllowSmsPaymentLink: false,
		};

		if (config?.monobankApiUrl) {
			payload.monobankApiUrl = config.monobankApiUrl;
		}

		if (config?.monobankCheck || isExecutorAndMonobank) {
			payload.monobankToken = config.monobankToken;

			payload.monobankFeePercentage = config.monobankFeePercentage;
			payload.monobankAdditionalFee = config.monobankAdditionalFee;
			payload.monobankGlobalFeeFromSystem =
				config.monobankGlobalFeeFromSystem;

			payload.monobankCurrency = config.monobankCurrency;
			payload.monobankCountryIso = config.monobankCountryIso;

			payload.systemReturnFeeToExecutor =
				config.systemReturnFeeToExecutor;
			payload.systemFeePercentageForExecutor =
				config.systemFeePercentageForExecutor;
			payload.systemFeeAdditionalForExecutor =
				config.systemFeeAdditionalForExecutor;

			payload.monobankCheck = config.monobankCheck;
			payload.monobankAction = config.monobankAction;

			payload.monobankAllowGooglePay =
				config.monobankAllowGooglePay ?? false;
			payload.monobankAllowApplePay =
				config.monobankAllowApplePay ?? false;
			payload.monobankAllowQrPayment =
				config.monobankAllowQrPayment ?? false;
			payload.monobankAllowSmsPaymentLink =
				config.monobankAllowSmsPaymentLink ?? false;
		}

		return payload;
	}

	public static fromResponseEasyPay(
		data: any,
	): PaymentSystem.EasyPayPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config =
			configuration as PaymentSystem.EasyPayPaymentConfiguration;

		const isTerminalEasyPay =
			config &&
			data.type === PaymentSystemFrontEndType.TERMINAL_EXECUTOR &&
			paymentProvider === PaymentProvider.EASYPAY;

		const payload: PaymentSystem.EasyPayPaymentConfiguration = {
			serviceId: "",
			useSign: false,
			easyPayPublicKey: "",
			providerPrivateKey: "",
			minAmount: undefined,
			maxAmount: undefined,
			easyPayAction: false,
		};

		if (config?.easyPayAction || isTerminalEasyPay || config?.serviceId) {
			payload.serviceId = config.serviceId || "";
			payload.useSign = !!config.useSign;
			payload.easyPayPublicKey = config.easyPayPublicKey || "";
			payload.providerPrivateKey = config.providerPrivateKey || "";
			payload.minAmount = config.minAmount;
			payload.maxAmount = config.maxAmount;
			payload.easyPayAction = !!config.easyPayAction;
		}

		return payload;
	}

	public static fromResponseOtv(
		data: any,
	): PaymentSystem.OtvPaymentConfiguration {
		const { configuration, paymentProvider } =
			data as PaymentSystem.Response;

		const config = configuration as PaymentSystem.OtvPaymentConfiguration;

		const isTerminalOtv =
			config &&
			data.type === PaymentSystemFrontEndType.TERMINAL_EXECUTOR &&
			paymentProvider === PaymentProvider.OTV;

		const payload: PaymentSystem.OtvPaymentConfiguration = {
			payType: "",
			minAmount: undefined,
			maxAmount: undefined,
			otvAction: false,
		};

		if (config?.otvAction || isTerminalOtv || config?.payType) {
			payload.payType = config.payType || "";
			payload.minAmount = config.minAmount;
			payload.maxAmount = config.maxAmount;
			payload.otvAction = !!config.otvAction;
		}

		return payload;
	}

	public static fromResponseBase(data: any): PaymentSystem.BaseModel {
		const { paymentProvider, paymentAccounts } =
			data as PaymentSystem.Response;

		const DEFAULT_NAME = "asset-payments_empty_customer";

		const payload: PaymentSystem.BaseModel = {
			id: data.id,
			name: data.name === DEFAULT_NAME ? "" : data.name,
			active: typeof data.active === "boolean" ? data.active : false,

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,

			isDefault: data.isDefault,
			type: data.type,

			withdrawMethod: data.withdrawMethod,
			defaultHoldValue: data.defaultHoldValue,
			refundPolicyUrl: data.refundPolicyUrl ?? "",

			paymentProvider,
			prePaymentProvider: paymentProvider,

			paymentAccounts,

			taxiServiceIds:
				compact(
					paymentAccounts?.map(({ taxiService }) => taxiService?.id),
				) || [],
			taxiServices:
				compact(
					paymentAccounts?.map(({ taxiService }) => taxiService),
				) || [],

			companyIds:
				compact(
					paymentAccounts?.map(({ company, taxiService }) => {
						if (company) return company?.id;
						return taxiService?.company?.id;
					}),
				) || [],

			companies:
				compact(
					paymentAccounts?.map(({ company, taxiService }) => {
						if (company) return company;
						return taxiService?.company;
					}),
				) || [],
		};

		return payload;
	}

	/**
	 * Поля комиссии водителю (`systemReturnFeeToExecutor`,
	 * `systemFeePercentageForExecutor`, `systemFeeAdditionalForExecutor`)
	 * общие для всех провайдеров и лежат в `configuration` один раз.
	 * Каждый `fromResponse<Provider>` возвращает для них дефолты, когда
	 * его провайдер не активен, поэтому при склейке в `fromResponse`
	 * последний спред перетирал реальное значение (например, у Asset
	 * галочка «компенсация водителю» после перезахода пропадала).
	 * Берём их напрямую из конфигурации.
	 */
	private static fromResponseSharedExecutorFee(data: any): Pick<
		PaymentSystem.AssetPaymentConfiguration,
		| "systemReturnFeeToExecutor"
		| "systemFeePercentageForExecutor"
		| "systemFeeAdditionalForExecutor"
	> {
		const configuration = (data as PaymentSystem.Response)
			?.configuration as
			| Partial<PaymentSystem.AssetPaymentConfiguration>
			| undefined;

		return {
			systemReturnFeeToExecutor:
				configuration?.systemReturnFeeToExecutor ?? false,
			systemFeePercentageForExecutor:
				configuration?.systemFeePercentageForExecutor ?? 0,
			systemFeeAdditionalForExecutor:
				configuration?.systemFeeAdditionalForExecutor ?? 0,
		};
	}

	public static fromResponse(data: any): PaymentSystem.Model {
		const payload: PaymentSystem.Model = {
			...PaymentSystem.fromResponseBase(data),
			...PaymentSystem.fromResponseAsset(data),
			...PaymentSystem.fromResponseMaid(data),
			...PaymentSystem.fromResponseDinar(data),
			...PaymentSystem.fromResponseBog(data),
			...PaymentSystem.fromResponseCybersource(data),
			...PaymentSystem.fromResponsePlaton(data),
			...PaymentSystem.fromResponseMonobank(data),
			...PaymentSystem.fromResponseEasyPay(data),
			...PaymentSystem.fromResponseOtv(data),
			...PaymentSystem.fromResponseSharedExecutorFee(data),
		};

		return payload;
	}

	public static toRequest(
		data: Omit<PaymentSystem.Modified, "id" | "updateDefault">,
	): Omit<PaymentSystem.Modified, "id" | "updateDefault"> {
		const DEFAULT_NAME = "Uncknow";

		const [isCustomer, isExecutor, isTerminalExecutor] = [
			data.type === PaymentSystemFrontEndType.CUSTOMER,
			data.type === PaymentSystemFrontEndType.EXECUTOR,
			data.type === PaymentSystemFrontEndType.TERMINAL_EXECUTOR,
		];

		let paymentProvider =
			data.paymentProvider ??
			data.prePaymentProvider ??
			PaymentProvider.ASSETPAYMENT;

		if (isTerminalExecutor) {
			if (data.paymentProvider === PaymentProvider.OTV) {
				paymentProvider = PaymentProvider.OTV;
			} else {
				paymentProvider = PaymentProvider.EASYPAY;
			}
		}

		const payload: Omit<PaymentSystem.Modified, "id" | "updateDefault"> & {
			configuration: any;
		} = {
			active: typeof data.active === "boolean" ? data.active : false,
			name: stringToUndefined(data.name) ?? DEFAULT_NAME,
			paymentProvider,
			type: data.type ?? PaymentSystemFrontEndType.CUSTOMER,

			withdrawMethod:
				data.withdrawMethod ?? WithdrawMethod.WITHDRAW_WITH_HOLD,

			defaultHoldValue: data.defaultHoldValue ?? 0.0,

			refundPolicyUrl: data.refundPolicyUrl?.trim() ?? "",

			configuration: {},
		};

		if (isTerminalExecutor) {
			payload.taxiServiceIds = data.taxiServiceIds;
			payload.paymentProvider = paymentProvider;

			const isActive =
				typeof data.active === "boolean" ? data.active : false;
			payload.active = isActive;

			if (paymentProvider === PaymentProvider.OTV) {
				const config = data as PaymentSystem.OtvPaymentConfiguration;
				payload.configuration.payType = (config.payType || "").trim();
				payload.configuration.minAmount = config.minAmount;
				payload.configuration.maxAmount = config.maxAmount;
				payload.configuration.otvAction =
					typeof data.active === "boolean"
						? data.active
						: !!config.otvAction;
			} else {
				const config = data as PaymentSystem.EasyPayPaymentConfiguration;
				payload.configuration.serviceId = (config.serviceId || "").trim();
				payload.configuration.useSign = !!config.useSign;
				payload.configuration.easyPayPublicKey =
					config.easyPayPublicKey || "";
				payload.configuration.providerPrivateKey =
					config.providerPrivateKey || "";
				payload.configuration.minAmount = config.minAmount;
				payload.configuration.maxAmount = config.maxAmount;
				payload.configuration.easyPayAction =
					typeof data.active === "boolean"
						? data.active
						: !!config.easyPayAction;
			}

			logger.info("[PaymentSystem] toRequest", {
				payload,
				data,
			});

			return payload;
		}

		if (isCustomer) {
			payload.companyIds = data.companyIds;

			if (data?.assetCheck) {
				const config = data as PaymentSystem.AssetPaymentConfiguration;

				payload.configuration.assetPaymentsGlobalFeeFromSystem =
					config.assetPaymentsGlobalFeeFromSystem;
				payload.configuration.assetPaymentsFeePercentage =
					config.assetPaymentsFeePercentage;
				payload.configuration.assetPaymentsAdditionalFee =
					config.assetPaymentsAdditionalFee;

				payload.configuration.assetPaymentsProcessingId =
					config.assetPaymentsProcessingId;

				payload.configuration.assetPaymentsCurrency =
					config.assetPaymentsCurrency;
				payload.configuration.assetPaymentsCountryIso =
					config.assetPaymentsCountryIso;
				payload.configuration.assetPaymentsKey =
					config.assetPaymentsKey.trim();
				payload.configuration.assetPaymentsSecretKey =
					config.assetPaymentsSecretKey.trim();

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.assetCheck = config.assetCheck;
				payload.configuration.assetAction = config.assetAction;
				payload.configuration.assetAllowQrPayment =
					config.assetAllowQrPayment ?? false;
				payload.configuration.assetAllowSmsPaymentLink =
					config.assetAllowSmsPaymentLink ?? false;
			}

			if (data?.maibCheck) {
				const config = data as PaymentSystem.MaibPaymentConfiguration;

				payload.configuration.maibPaymentsKey = config.maibPaymentsKey;
				payload.configuration.maibPaymentsSecretKey =
					config.maibPaymentsSecretKey;
				payload.configuration.maibSignatureId = config.maibSignatureId;

				payload.configuration.maibPaymentsCurrency =
					config.maibPaymentsCurrency;
				payload.configuration.maibPaymentsCountryIso =
					config.maibPaymentsCountryIso;

				payload.configuration.maibCheck = config.maibCheck;
				payload.configuration.maibAction = config.maibAction;

				payload.configuration.maibWithdrawMethod =
					WithdrawMethod.WITHDRAW_WITH_HOLD;
			}

			if (data?.dinarPayCheck) {
				const config = data as PaymentSystem.DinarPaymentConfiguration;

				payload.configuration.dinarPayUsername =
					config.dinarPayUsername.trim();
				payload.configuration.dinarPayPassword =
					config.dinarPayPassword.trim();
				payload.configuration.dinarPayApiUrl =
					config.dinarPayApiUrl.trim();
				payload.configuration.dinarPayCertificate =
					config.dinarPayCertificate?.trim() || "";
				payload.configuration.dinarPayCertPassword =
					config.dinarPayCertPassword?.trim() || "";

				payload.configuration.dinarPayFeePercentage =
					config.dinarPayFeePercentage;
				payload.configuration.dinarPayAdditionalFee =
					config.dinarPayAdditionalFee;
				payload.configuration.dinarPayGlobalFeeFromSystem =
					config.dinarPayGlobalFeeFromSystem;

				payload.configuration.dinarPayCurrency =
					config.dinarPayCurrency;
				payload.configuration.dinarPayCountryIso =
					config.dinarPayCountryIso;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.dinarPayCheck = config.dinarPayCheck;
				payload.configuration.dinarPayAction = config.dinarPayAction;
				payload.configuration.dinarPayWithdrawMethod =
					config.dinarPayWithdrawMethod;
			}

			if (data?.bogCheck) {
				const config = data as PaymentSystem.BogPaymentConfiguration;

				payload.configuration.bogClientId = config.bogClientId.trim();
				payload.configuration.bogClientSecret =
					config.bogClientSecret.trim();
				payload.configuration.bogApiUrl = config.bogApiUrl.trim();

				payload.configuration.bogFeePercentage =
					config.bogFeePercentage;
				payload.configuration.bogAdditionalFee =
					config.bogAdditionalFee;
				payload.configuration.bogGlobalFeeFromSystem =
					config.bogGlobalFeeFromSystem;

				payload.configuration.bogCurrency = config.bogCurrency;
				payload.configuration.bogCountryIso = config.bogCountryIso;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.bogCheck = config.bogCheck;
				payload.configuration.bogAction = config.bogAction;
				payload.configuration.bogWithdrawMethod =
					config.bogWithdrawMethod;
			}

			if (data?.cybersourceCheck) {
				const config =
					data as PaymentSystem.CybersourcePaymentConfiguration;

				payload.configuration.cybersourceMerchantId =
					config.cybersourceMerchantId.trim();
				payload.configuration.cybersourceApiKeyId =
					config.cybersourceApiKeyId.trim();
				payload.configuration.cybersourceApiSecret =
					config.cybersourceApiSecret.trim();
				payload.configuration.cybersourceApiUrl =
					config.cybersourceApiUrl.trim();
				payload.configuration.cybersourceOrganizationId = (
					config.cybersourceOrganizationId ?? ""
				).trim();

				payload.configuration.cybersourceFeePercentage =
					config.cybersourceFeePercentage;
				payload.configuration.cybersourceAdditionalFee =
					config.cybersourceAdditionalFee;
				payload.configuration.cybersourceGlobalFeeFromSystem =
					config.cybersourceGlobalFeeFromSystem;

				payload.configuration.cybersourceCurrency =
					config.cybersourceCurrency;
				payload.configuration.cybersourceCountryIso =
					config.cybersourceCountryIso;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.cybersourceCheck =
					config.cybersourceCheck;
				payload.configuration.cybersourceAction =
					config.cybersourceAction;
				payload.configuration.cybersourceWithdrawMethod =
					config.cybersourceWithdrawMethod;

				// Trip report settings — customer payment system (Victoriabank).
				payload.configuration.cybersourceCreateTripReports =
					config.cybersourceCreateTripReports ?? true;
				payload.configuration.cybersourceMerchantLegalName = (
					config.cybersourceMerchantLegalName ?? ""
				).trim();
				payload.configuration.cybersourceMerchantWebsite = (
					config.cybersourceMerchantWebsite ?? ""
				).trim();
				payload.configuration.cybersourceSupportContacts = (
					config.cybersourceSupportContacts ?? ""
				).trim();
			}

			if (data?.platonCheck) {
				const config = data as PaymentSystem.PlatonPaymentConfiguration;

				payload.configuration.platonClientKey =
					config.platonClientKey.trim();
				payload.configuration.platonClientPassword =
					config.platonClientPassword.trim();
				payload.configuration.platonApiUrl = config.platonApiUrl.trim();

				payload.configuration.platonFeePercentage =
					config.platonFeePercentage;
				payload.configuration.platonAdditionalFee =
					config.platonAdditionalFee;
				payload.configuration.platonGlobalFeeFromSystem =
					config.platonGlobalFeeFromSystem;

				payload.configuration.platonCurrency = config.platonCurrency;
				payload.configuration.platonCountryIso =
					config.platonCountryIso;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.platonPayoutToExecutorCardEnabled =
					config.platonPayoutToExecutorCardEnabled;

				payload.configuration.platonAllowGooglePay =
					config.platonAllowGooglePay ?? false;
				payload.configuration.platonAllowApplePay =
					config.platonAllowApplePay ?? false;

				payload.configuration.platonCheck = config.platonCheck;
				payload.configuration.platonAction = config.platonAction;
				payload.configuration.platonWithdrawMethod =
					config.platonWithdrawMethod;
			}

			if (data?.monobankCheck) {
				const config =
					data as PaymentSystem.MonobankPaymentConfiguration;

				payload.configuration.monobankToken =
					config.monobankToken.trim();
				payload.configuration.monobankApiUrl =
					config.monobankApiUrl.trim();

				payload.configuration.monobankFeePercentage =
					config.monobankFeePercentage;
				payload.configuration.monobankAdditionalFee =
					config.monobankAdditionalFee;
				payload.configuration.monobankGlobalFeeFromSystem =
					config.monobankGlobalFeeFromSystem;

				payload.configuration.monobankCurrency =
					config.monobankCurrency;
				payload.configuration.monobankCountryIso =
					config.monobankCountryIso;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.monobankAllowGooglePay =
					config.monobankAllowGooglePay ?? false;
				payload.configuration.monobankAllowApplePay =
					config.monobankAllowApplePay ?? false;
				payload.configuration.monobankAllowQrPayment =
					config.monobankAllowQrPayment ?? false;
				payload.configuration.monobankAllowSmsPaymentLink =
					config.monobankAllowSmsPaymentLink ?? false;

				payload.configuration.monobankCheck = config.monobankCheck;
				payload.configuration.monobankAction = config.monobankAction;
				payload.configuration.monobankWithdrawMethod =
					config.monobankWithdrawMethod;
			}

			if (data.maibAction) {
				payload.paymentProvider = PaymentProvider.MAIB;
			}
			if (data.assetAction) {
				payload.paymentProvider = PaymentProvider.ASSETPAYMENT;
			}
			if (data.dinarPayAction) {
				payload.paymentProvider = PaymentProvider.DINARPAY;
			}
			if (data.bogAction) {
				payload.paymentProvider = PaymentProvider.BOG;
			}
			if (data.cybersourceAction) {
				payload.paymentProvider = PaymentProvider.CYBERSOURCE;
			}
			if (data.platonAction) {
				payload.paymentProvider = PaymentProvider.PLATON;
			}
			if (data.monobankAction) {
				payload.paymentProvider = PaymentProvider.MONOBANK;
			}
			if (
				!data.maibCheck &&
				!data.assetCheck &&
				!data.dinarPayCheck &&
				!data.bogCheck &&
				!data.cybersourceCheck &&
				!data.platonCheck &&
				!data.monobankCheck
			) {
				payload.active = false;
			}
		}

		if (isExecutor) {
			payload.taxiServiceIds = data.taxiServiceIds;
			if (payload.paymentProvider === PaymentProvider.ASSETPAYMENT) {
				const config = data as PaymentSystem.AssetPaymentConfiguration;

				payload.configuration.assetPaymentsGlobalFeeFromSystem =
					config.assetPaymentsGlobalFeeFromSystem;
				payload.configuration.assetPaymentsFeePercentage =
					config.assetPaymentsFeePercentage;
				payload.configuration.assetPaymentsAdditionalFee =
					config.assetPaymentsAdditionalFee;

				payload.configuration.assetPaymentsProcessingId =
					config.assetPaymentsProcessingId;

				payload.configuration.assetPaymentsCurrency =
					config.assetPaymentsCurrency;
				payload.configuration.assetPaymentsCountryIso =
					config.assetPaymentsCountryIso;
				payload.configuration.assetPaymentsKey =
					config.assetPaymentsKey.trim();
				payload.configuration.assetPaymentsSecretKey =
					config.assetPaymentsSecretKey.trim();

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.assetCheck = config.assetCheck;
				payload.configuration.assetAction = config.assetAction;
				payload.configuration.assetAllowQrPayment =
					config.assetAllowQrPayment ?? false;
				payload.configuration.assetAllowSmsPaymentLink =
					config.assetAllowSmsPaymentLink ?? false;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;

				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;
			}

			if (payload.paymentProvider === PaymentProvider.MAIB) {
				const config = data as PaymentSystem.MaibPaymentConfiguration;

				payload.configuration.maibPaymentsKey = config.maibPaymentsKey;
				payload.configuration.maibPaymentsSecretKey =
					config.maibPaymentsSecretKey;
				payload.configuration.maibSignatureId = config.maibSignatureId;

				payload.configuration.maibPaymentsCurrency =
					config.maibPaymentsCurrency;
				payload.configuration.maibPaymentsCountryIso =
					config.maibPaymentsCountryIso;

				payload.configuration.maibCheck = config.maibCheck;
				payload.configuration.maibAction = config.maibAction;

				payload.configuration.maibWithdrawMethod =
					WithdrawMethod.WITHDRAW_WITH_HOLD;
			}

			if (payload.paymentProvider === PaymentProvider.DINARPAY) {
				const config = data as PaymentSystem.DinarPaymentConfiguration;

				payload.configuration.dinarPayUsername =
					config.dinarPayUsername.trim();
				payload.configuration.dinarPayPassword =
					config.dinarPayPassword.trim();
				payload.configuration.dinarPayApiUrl =
					config.dinarPayApiUrl.trim();
				payload.configuration.dinarPayCertificate =
					config.dinarPayCertificate?.trim() || "";
				payload.configuration.dinarPayCertPassword =
					config.dinarPayCertPassword?.trim() || "";

				payload.configuration.dinarPayFeePercentage =
					config.dinarPayFeePercentage;
				payload.configuration.dinarPayAdditionalFee =
					config.dinarPayAdditionalFee;
				payload.configuration.dinarPayGlobalFeeFromSystem =
					config.dinarPayGlobalFeeFromSystem;

				payload.configuration.dinarPayCurrency =
					config.dinarPayCurrency;
				payload.configuration.dinarPayCountryIso =
					config.dinarPayCountryIso;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;
				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;

				payload.configuration.dinarPayCheck =
					config.dinarPayCheck ?? true;
				payload.configuration.dinarPayAction =
					config.dinarPayAction ?? true;
				if (config.dinarPayWithdrawMethod !== undefined) {
					payload.configuration.dinarPayWithdrawMethod =
						config.dinarPayWithdrawMethod;
				}
			}

			if (payload.paymentProvider === PaymentProvider.BOG) {
				const config = data as PaymentSystem.BogPaymentConfiguration;

				payload.configuration.bogClientId = config.bogClientId.trim();
				payload.configuration.bogClientSecret =
					config.bogClientSecret.trim();
				payload.configuration.bogApiUrl = config.bogApiUrl.trim();

				payload.configuration.bogFeePercentage =
					config.bogFeePercentage;
				payload.configuration.bogAdditionalFee =
					config.bogAdditionalFee;
				payload.configuration.bogGlobalFeeFromSystem =
					config.bogGlobalFeeFromSystem;

				payload.configuration.bogCurrency = config.bogCurrency;
				payload.configuration.bogCountryIso = config.bogCountryIso;
				payload.configuration.bogCheck = config.bogCheck;
				payload.configuration.bogAction = config.bogAction;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;
				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;
			}

			if (payload.paymentProvider === PaymentProvider.CYBERSOURCE) {
				const config =
					data as PaymentSystem.CybersourcePaymentConfiguration;

				payload.configuration.cybersourceMerchantId =
					config.cybersourceMerchantId.trim();
				payload.configuration.cybersourceApiKeyId =
					config.cybersourceApiKeyId.trim();
				payload.configuration.cybersourceApiSecret =
					config.cybersourceApiSecret.trim();
				payload.configuration.cybersourceApiUrl =
					config.cybersourceApiUrl.trim();
				payload.configuration.cybersourceOrganizationId = (
					config.cybersourceOrganizationId ?? ""
				).trim();

				payload.configuration.cybersourceFeePercentage =
					config.cybersourceFeePercentage;
				payload.configuration.cybersourceAdditionalFee =
					config.cybersourceAdditionalFee;
				payload.configuration.cybersourceGlobalFeeFromSystem =
					config.cybersourceGlobalFeeFromSystem;

				payload.configuration.cybersourceCurrency =
					config.cybersourceCurrency;
				payload.configuration.cybersourceCountryIso =
					config.cybersourceCountryIso;
				payload.configuration.cybersourceCheck =
					config.cybersourceCheck;
				payload.configuration.cybersourceAction =
					config.cybersourceAction;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;
				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;

				payload.configuration.cybersourceCreateTripReports =
					config.cybersourceCreateTripReports ?? true;
				payload.configuration.cybersourceMerchantLegalName = (
					config.cybersourceMerchantLegalName ?? ""
				).trim();
				payload.configuration.cybersourceMerchantWebsite = (
					config.cybersourceMerchantWebsite ?? ""
				).trim();
				payload.configuration.cybersourceSupportContacts = (
					config.cybersourceSupportContacts ?? ""
				).trim();
			}

			if (payload.paymentProvider === PaymentProvider.PLATON) {
				const config = data as PaymentSystem.PlatonPaymentConfiguration;

				payload.configuration.platonClientKey =
					config.platonClientKey.trim();
				payload.configuration.platonClientPassword =
					config.platonClientPassword.trim();
				payload.configuration.platonApiUrl = config.platonApiUrl.trim();

				payload.configuration.platonFeePercentage =
					config.platonFeePercentage;
				payload.configuration.platonAdditionalFee =
					config.platonAdditionalFee;
				payload.configuration.platonGlobalFeeFromSystem =
					config.platonGlobalFeeFromSystem;

				payload.configuration.platonCurrency = config.platonCurrency;
				payload.configuration.platonCountryIso =
					config.platonCountryIso;
				payload.configuration.platonCheck = config.platonCheck;
				payload.configuration.platonAction = config.platonAction;

				payload.configuration.platonPayoutToExecutorCardEnabled =
					config.platonPayoutToExecutorCardEnabled;

				payload.configuration.platonAllowGooglePay =
					config.platonAllowGooglePay ?? false;
				payload.configuration.platonAllowApplePay =
					config.platonAllowApplePay ?? false;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;
				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;
			}

			if (payload.paymentProvider === PaymentProvider.MONOBANK) {
				const config =
					data as PaymentSystem.MonobankPaymentConfiguration;

				payload.configuration.monobankToken =
					config.monobankToken.trim();
				payload.configuration.monobankApiUrl =
					config.monobankApiUrl.trim();

				payload.configuration.monobankFeePercentage =
					config.monobankFeePercentage;
				payload.configuration.monobankAdditionalFee =
					config.monobankAdditionalFee;
				payload.configuration.monobankGlobalFeeFromSystem =
					config.monobankGlobalFeeFromSystem;

				payload.configuration.monobankCurrency =
					config.monobankCurrency;
				payload.configuration.monobankCountryIso =
					config.monobankCountryIso;
				payload.configuration.monobankCheck = config.monobankCheck;
				payload.configuration.monobankAction = config.monobankAction;

				payload.configuration.monobankAllowGooglePay =
					config.monobankAllowGooglePay ?? false;
				payload.configuration.monobankAllowApplePay =
					config.monobankAllowApplePay ?? false;
				payload.configuration.monobankAllowQrPayment =
					config.monobankAllowQrPayment ?? false;
				payload.configuration.monobankAllowSmsPaymentLink =
					config.monobankAllowSmsPaymentLink ?? false;

				payload.configuration.systemReturnFeeToExecutor =
					config.systemReturnFeeToExecutor;

				payload.configuration.systemFeeAdditionalForExecutor =
					config.systemFeeAdditionalForExecutor;
				payload.configuration.systemFeePercentageForExecutor =
					config.systemFeePercentageForExecutor;
			}

			if (data.maibAction) {
				payload.paymentProvider = PaymentProvider.MAIB;
			}
			if (data.assetAction) {
				payload.paymentProvider = PaymentProvider.ASSETPAYMENT;
			}
			if (data.dinarPayAction) {
				payload.paymentProvider = PaymentProvider.DINARPAY;
			}
			if (data.bogAction) {
				payload.paymentProvider = PaymentProvider.BOG;
			}
			if (data.cybersourceAction) {
				payload.paymentProvider = PaymentProvider.CYBERSOURCE;
			}
			if (data.platonAction) {
				payload.paymentProvider = PaymentProvider.PLATON;
			}
			if (data.monobankAction) {
				payload.paymentProvider = PaymentProvider.MONOBANK;
			}
			if (
				!data.maibCheck &&
				!data.assetCheck &&
				!data.dinarPayCheck &&
				!data.bogCheck &&
				!data.cybersourceCheck &&
				!data.platonCheck &&
				!data.monobankCheck
			) {
				payload.active = false;
			}
		}

		logger.info("[PaymentSystem] toRequest", {
			payload,
			data,
		});

		return payload;
	}

	public static toRequestForCheckConnection(
		data: Omit<PaymentSystem.Modified, "id" | "updateDefault">,
	): PaymentSystem.CheckConnectionRequestPayload | null {
		const paymentProvider =
			data.paymentProvider ??
			data.prePaymentProvider ??
			PaymentProvider.ASSETPAYMENT;

		if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
			const config = data as PaymentSystem.AssetPaymentConfiguration;

			const payload: PaymentSystem.AssetCheckConnection = {
				provider: PaymentProvider.ASSETPAYMENT,
				configuration: {
					assetPaymentsKey: config.assetPaymentsKey.trim(),
					assetPaymentsSecretKey:
						config.assetPaymentsSecretKey.trim(),
					assetPaymentsProcessingId: config.assetPaymentsProcessingId,
					assetPaymentsCurrency: config.assetPaymentsCurrency,
					assetPaymentsCountryIso: config.assetPaymentsCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (paymentProvider === PaymentProvider.MAIB) {
			const config = data as PaymentSystem.MaibPaymentConfiguration;

			const payload: PaymentSystem.MaibCheckConnection = {
				provider: PaymentProvider.MAIB,
				configuration: {
					maibPaymentsKey: config.maibPaymentsKey,
					maibPaymentsSecretKey: config.maibPaymentsSecretKey,
					maibSignatureId: config.maibSignatureId,
					maibPaymentsCurrency: config.maibPaymentsCurrency,
					maibPaymentsCountryIso: config.maibPaymentsCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (paymentProvider === PaymentProvider.DINARPAY) {
			const config = data as PaymentSystem.DinarPaymentConfiguration;

			const payload: PaymentSystem.DinarCheckConnection = {
				provider: PaymentProvider.DINARPAY,
				configuration: {
					dinarPayUsername: config.dinarPayUsername.trim(),
					dinarPayPassword: config.dinarPayPassword.trim(),
					dinarPayApiUrl: config.dinarPayApiUrl.trim(),
					dinarPayCertificate:
						config.dinarPayCertificate?.trim() || "",
					dinarPayCertPassword:
						config.dinarPayCertPassword?.trim() || "",
					dinarPayCurrency: config.dinarPayCurrency,
					dinarPayCountryIso: config.dinarPayCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (paymentProvider === PaymentProvider.BOG) {
			const config = data as PaymentSystem.BogPaymentConfiguration;

			const payload: PaymentSystem.BogCheckConnection = {
				provider: PaymentProvider.BOG,
				configuration: {
					bogClientId: config.bogClientId.trim(),
					bogClientSecret: config.bogClientSecret.trim(),
					bogApiUrl: config.bogApiUrl.trim(),
					bogCurrency: config.bogCurrency,
					bogCountryIso: config.bogCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (paymentProvider === PaymentProvider.CYBERSOURCE) {
			const config =
				data as PaymentSystem.CybersourcePaymentConfiguration;

			const payload: PaymentSystem.CybersourceCheckConnection = {
				provider: PaymentProvider.CYBERSOURCE,
				configuration: {
					cybersourceMerchantId: config.cybersourceMerchantId.trim(),
					cybersourceApiKeyId: config.cybersourceApiKeyId.trim(),
					cybersourceApiSecret: config.cybersourceApiSecret.trim(),
					cybersourceApiUrl: config.cybersourceApiUrl.trim(),
					cybersourceCurrency: config.cybersourceCurrency,
					cybersourceCountryIso: config.cybersourceCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (paymentProvider === PaymentProvider.PLATON) {
			const config = data as PaymentSystem.PlatonPaymentConfiguration;

			const payload: PaymentSystem.PlatonCheckConnection = {
				provider: PaymentProvider.PLATON,
				configuration: {
					platonClientKey: config.platonClientKey.trim(),
					platonClientPassword: config.platonClientPassword.trim(),
					platonApiUrl: config.platonApiUrl.trim(),
					platonCurrency: config.platonCurrency,
					platonCountryIso: config.platonCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		if (paymentProvider === PaymentProvider.MONOBANK) {
			const config = data as PaymentSystem.MonobankPaymentConfiguration;

			const payload: PaymentSystem.MonobankCheckConnection = {
				provider: PaymentProvider.MONOBANK,
				configuration: {
					monobankToken: config.monobankToken.trim(),
					monobankApiUrl: config.monobankApiUrl.trim(),
					monobankCurrency: config.monobankCurrency,
					monobankCountryIso: config.monobankCountryIso,
				},
			};

			logger.info("[PaymentSystem] toRequestForCheckConnection", {
				payload,
				data,
			});

			return payload;
		}

		return null;
	}

	public static async connectCheck(params: PaymentSystem.Model): Promise<{
		result: boolean | null;
		error: string | null;
	}> {
		try {
			const data = this.toRequestForCheckConnection(params);

			if (!data) {
				logger.info("[PaymentSystem] connectCheck", null);
				return { result: null, error: "don`n normal formating data" };
			}

			logger.info("[PaymentSystem] connectCheck data", data);

			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.checkConnection(data),
			);

			const retval = {
				result: res?.success || null,
				error: res?.message || null,
			};

			logger.info("[PaymentSystem] connectCheck", retval);
			return retval;
		} catch (err) {
			const errorMessage = stringifyError(err);
			logger.error("[PaymentSystem] Error connectCheck", errorMessage);
			return { result: null, error: errorMessage };
		}
	}

	public static async update({
		id,
		updateDefault = false,
		...params
	}: PaymentSystem.Modified): Promise<{
		result: PaymentSystem.Model | null;
		error: string | null;
	}> {
		try {
			if (!id) return { result: null, error: "Missing ID" };
			const toRequest = this.toRequest(params);
			logger.info("[PaymentSystem]  update params", {
				id,
				params,
				toRequest,
			});

			const result = await this.request((prpc) =>
				prpc.theirsModel.payment.system.update(
					id,
					toRequest,
					updateDefault,
				),
			);
			logger.info("[PaymentSystem] update res", {
				result,
				params,
				toRequest,
			});
			return { result, error: null };
		} catch (err) {
			const errorMessage = stringifyError(err);
			logger.error("[PaymentSystem] Error update", errorMessage);
			return { result: null, error: errorMessage };
		}
	}

	public static async create({
		id,
		...params
	}: PaymentSystem.Modified): Promise<{
		result: PaymentSystem.Model | null;
		error: string | null;
	}> {
		try {
			const toRequest = this.toRequest(params);
			logger.info("[PaymentSystem]  create params", {
				id,
				params,
				toRequest,
			});

			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.create(toRequest, {
					templateId: id ?? 0,
				}),
			);
			logger.info("[PaymentSystem]  create res", {
				res,
				params,
				toRequest,
			});
			return { result: res, error: null };
		} catch (err) {
			const errorMessage = stringifyError(err);
			logger.error("[PaymentSystem] Error create", errorMessage);
			return { result: null, error: errorMessage };
		}
	}

	public static async store(id: number): Promise<PaymentSystem.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.getById(id),
			);

			return res;
		} catch (err) {
			logger.error("[PaymentSystem] Error store", err);
			return null;
		}
	}

	public static async copy(id: number): Promise<PaymentSystem.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.copy(id),
			);
			logger.info("[PaymentSystem]  copy res", res);
			return res;
		} catch (err) {
			logger.error("[PaymentSystem] Error copy", err);
			return null;
		}
	}

	public static async destroy(
		ids: number[],
	): Promise<PaymentSystem.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.payment.system.delete(ids),
			);

			return res;
		} catch (err) {
			logger.error("[PaymentSystem] Error destroy", err);
			return null;
		}
	}

	public static async subscribe(
		options: PaymentSystem.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<PaymentSystem.Model>,
	): Promise<Subscription<PaymentSystem.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[PaymentSystem] subscribe", state);
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});
		const params = this.optionsToRequest(options);
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.payment.system.subscribe({
					params,
					ping: () => true,
					onEvent: async (events) => {
						await modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error("[PaymentSystem] Error subscribe", error);
					},
				}),
			{
				name: "PaymentSystem.subscribe",
				metadata: params,
			},
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (subOptions: PaymentSystem.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(subOptions)),
		} as Subscription<PaymentSystem.SubscribeOptions>;
	}
}
declare namespace PaymentSystem {
	interface BaseModel
		extends NonEditableProperties,
			Pick<
				Response,
				| "active"
				| "isDefault"
				| "type"
				| "paymentProvider"
				| "prePaymentProvider"
			> {
		name: string;

		paymentAccounts: PaymentAccount[];

		taxiServiceIds: number[];
		taxiServices: PaymentAccount["taxiService"][];

		companyIds: number[];
		companies: PaymentAccount["company"][];

		updateDefault?: boolean;

		withdrawMethod: WithdrawMethod;
		defaultHoldValue: number;
		/** Ссылка на политику возврата, показывается в клиентском приложении. */
		refundPolicyUrl: string;
	}

	interface Model
		extends BaseModel,
			AssetPaymentConfiguration,
			MaibPaymentConfiguration,
			DinarPaymentConfiguration,
			BogPaymentConfiguration,
			CybersourcePaymentConfiguration,
			PlatonPaymentConfiguration,
			MonobankPaymentConfiguration,
			EasyPayPaymentConfiguration,
			OtvPaymentConfiguration {}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<PaymentSystem.Model>,
			"query"
		> {
		types: PaymentSystemFrontEndType[];
		/** Only for `executor` */
		taxiServiceIds?: number[];
		/** Only for `customer` */
		companyIds?: number[];
		language?: Lang;
	}

	type NonEditablePropertyNames =
		| "id"
		| "createdAt"
		| "updatedAt"
		| "deletedAt";

	interface NonEditableProperties {
		readonly id: ModelId;
		readonly createdAt: string;
		readonly updatedAt: string;
		readonly deletedAt: string | null;
	}

	type New = Omit<Model, NonEditablePropertyNames>;
	type Modified = Partial<New> & Pick<NonEditableProperties, "id">;

	interface PaymentAccount {
		company: { id: number; name: Lang };
		taxiService: {
			id: number;
			settlement: Lang;
			company: { id: number; name: Lang };
		};
	}

	type PaymentAccounts = PaymentAccount[];

	interface Response extends NonEditableProperties {
		active: boolean;
		configuration:
			| AssetPaymentConfiguration
			| MaibPaymentConfiguration
			| DinarPaymentConfiguration
			| BogPaymentConfiguration
			| CybersourcePaymentConfiguration
			| PlatonPaymentConfiguration
			| MonobankPaymentConfiguration
			| EasyPayPaymentConfiguration
			| OtvPaymentConfiguration;
		isDefault: boolean;
		name: string;

		withdrawMethod: WithdrawMethod;
		defaultHoldValue: number;
		refundPolicyUrl: string | null;

		paymentAccounts: PaymentAccounts;
		paymentProvider: PaymentProvider;
		prePaymentProvider: PaymentProvider;
		type: PaymentSystemFrontEndType;

		v: number;
	}
	interface BasePaymentConfig {
		systemReturnFeeToExecutor: boolean;
		systemFeePercentageForExecutor: number;
		systemFeeAdditionalForExecutor: number;
	}

	interface AssetPaymentConfiguration extends BasePaymentConfig {
		assetPaymentsKey: string;
		assetPaymentsSecretKey: string;

		assetPaymentsProcessingId: number;
		assetPaymentsFeePercentage: number;
		assetPaymentsAdditionalFee: number;
		assetPaymentsGlobalFeeFromSystem: boolean;

		assetPaymentsCurrency: PaymentCurrency;
		assetPaymentsCountryIso: PaymentCountryIso;

		assetCheck: boolean;
		assetAction: boolean;

		/**
		 * Разрешить разовую оплату заказа по ссылке/QR: водитель в конце
		 * поездки выпускает инвойс Asset, клиент платит на странице Asset.
		 * По этому флагу приложение водителя показывает кнопку.
		 */
		assetAllowQrPayment?: boolean;
		/**
		 * Разрешить диспетчеру отправлять ту же ссылку на оплату по SMS.
		 * Отдельно от QR: можно слать по SMS и не показывать QR водителю.
		 */
		assetAllowSmsPaymentLink?: boolean;
	}

	interface MaibPaymentConfiguration extends BasePaymentConfig {
		maibPaymentsKey: string;
		maibPaymentsSecretKey: string;
		maibSignatureId: string;

		maibPaymentsCurrency: MaibPaymentCurrency;
		maibPaymentsCountryIso: MaibPaymentCountryIso;

		maibCheck: boolean;
		maibAction: boolean;

		maibWithdrawMethod: WithdrawMethod;
	}

	interface DinarPaymentConfiguration extends BasePaymentConfig {
		dinarPayUsername: string;
		dinarPayPassword: string;
		dinarPayApiUrl: string;

		/** Сертификат аккаунта .p12 (PFX) в base64, mTLS для api.cibpay.co. */
		dinarPayCertificate: string;
		/** Пароль сертификата .p12. */
		dinarPayCertPassword: string;

		dinarPayFeePercentage: number;
		dinarPayAdditionalFee: number;
		dinarPayGlobalFeeFromSystem: boolean;

		dinarPayCurrency: PaymentCurrency;
		dinarPayCountryIso: PaymentCountryIso;

		dinarPayCheck: boolean;
		dinarPayAction: boolean;

		dinarPayWithdrawMethod: WithdrawMethod;
	}

	interface BogPaymentConfiguration extends BasePaymentConfig {
		bogClientId: string;
		bogClientSecret: string;
		bogApiUrl: string;

		bogFeePercentage: number;
		bogAdditionalFee: number;
		bogGlobalFeeFromSystem: boolean;

		bogCurrency: PaymentCurrency;
		bogCountryIso: PaymentCountryIso;

		bogCheck: boolean;
		bogAction: boolean;

		bogWithdrawMethod: WithdrawMethod;
	}

	interface CybersourcePaymentConfiguration extends BasePaymentConfig {
		cybersourceMerchantId: string;
		cybersourceApiKeyId: string;
		cybersourceApiSecret: string;
		cybersourceApiUrl: string;
		cybersourceOrganizationId?: string;

		cybersourceFeePercentage: number;
		cybersourceAdditionalFee: number;
		cybersourceGlobalFeeFromSystem: boolean;

		cybersourceCurrency: PaymentCurrency;
		cybersourceCountryIso: PaymentCountryIso;

		cybersourceCheck: boolean;
		cybersourceAction: boolean;

		cybersourceWithdrawMethod: WithdrawMethod;

		/**
		 * Создание отчетов о поездке (банковский чек Victoriabank).
		 * undefined трактуется как включено.
		 */
		cybersourceCreateTripReports?: boolean;

		/** Юридическое наименование мерчанта для отчета о поездке. */
		cybersourceMerchantLegalName?: string;

		/** Адрес веб-сайта мерчанта. */
		cybersourceMerchantWebsite?: string;

		/** Контакты службы поддержки клиентов. */
		cybersourceSupportContacts?: string;
	}

	interface PlatonPaymentConfiguration extends BasePaymentConfig {
		platonClientKey: string;
		platonClientPassword: string;
		platonApiUrl: string;

		platonFeePercentage: number;
		platonAdditionalFee: number;
		platonGlobalFeeFromSystem: boolean;

		platonCurrency: PaymentCurrency;
		platonCountryIso: PaymentCountryIso;

		platonCheck: boolean;
		platonAction: boolean;

		platonWithdrawMethod: WithdrawMethod;

		/**
		 * Кому идет доход от поездки: true — водителю на карту,
		 * false — службе такси.
		 */
		platonPayoutToExecutorCardEnabled?: boolean;

		/** Разрешить оплату через Google Pay. */
		platonAllowGooglePay?: boolean;

		/** Разрешить оплату через Apple Pay. */
		platonAllowApplePay?: boolean;
	}

	interface MonobankPaymentConfiguration extends BasePaymentConfig {
		/** Merchant-токен monobank (`X-Token`) — единственный секрет. */
		monobankToken: string;
		monobankApiUrl: string;

		monobankFeePercentage: number;
		monobankAdditionalFee: number;
		monobankGlobalFeeFromSystem: boolean;

		monobankCurrency: PaymentCurrency;
		monobankCountryIso: PaymentCountryIso;

		monobankCheck: boolean;
		monobankAction: boolean;

		monobankWithdrawMethod: WithdrawMethod;

		/** Разрешить оплату через Google Pay. */
		monobankAllowGooglePay?: boolean;

		/** Разрешить оплату через Apple Pay. */
		monobankAllowApplePay?: boolean;

		/**
		 * Разрешить разовую оплату заказа по ссылке/QR: водитель в конце
		 * поездки выпускает ссылку, клиент платит на странице monobank.
		 * По этому флагу приложение водителя показывает кнопку.
		 */
		monobankAllowQrPayment?: boolean;
		/**
		 * Разрешить диспетчеру отправлять ту же ссылку на оплату по SMS.
		 * Отдельно от QR: можно слать по SMS и не показывать QR водителю.
		 */
		monobankAllowSmsPaymentLink?: boolean;
	}

	interface EasyPayPaymentConfiguration {
		serviceId: string;
		useSign: boolean;
		easyPayPublicKey?: string;
		providerPrivateKey?: string;
		minAmount?: number;
		maxAmount?: number;
		easyPayAction: boolean;
	}

	interface OtvPaymentConfiguration {
		payType: string;
		minAmount?: number;
		maxAmount?: number;
		otvAction: boolean;
	}

	export interface AssetPaymentConfigurationPayload {
		assetPaymentsKey: string;
		assetPaymentsSecretKey: string;
		assetPaymentsProcessingId: number;
		assetPaymentsCurrency: string;
		assetPaymentsCountryIso: string;
	}

	export interface MaibPaymentConfigurationPayload {
		maibPaymentsKey: string;
		maibPaymentsSecretKey: string;
		maibSignatureId: string;
		maibPaymentsCurrency: string;
		maibPaymentsCountryIso: string;
	}

	interface AssetCheckConnection {
		provider: PaymentProvider.ASSETPAYMENT;
		configuration: AssetPaymentConfigurationPayload;
	}

	interface MaibCheckConnection {
		provider: PaymentProvider.MAIB;
		configuration: MaibPaymentConfigurationPayload;
	}
	interface DinarCheckConnection {
		provider: PaymentProvider.DINARPAY;
		configuration: {
			dinarPayUsername: string;
			dinarPayPassword: string;
			dinarPayApiUrl: string;
			dinarPayCertificate: string;
			dinarPayCertPassword: string;
			dinarPayCurrency: PaymentCurrency;
			dinarPayCountryIso: PaymentCountryIso;
		};
	}
	interface BogCheckConnection {
		provider: PaymentProvider.BOG;
		configuration: {
			bogClientId: string;
			bogClientSecret: string;
			bogApiUrl: string;
			bogCurrency: PaymentCurrency;
			bogCountryIso: PaymentCountryIso;
		};
	}
	interface CybersourceCheckConnection {
		provider: PaymentProvider.CYBERSOURCE;
		configuration: {
			cybersourceMerchantId: string;
			cybersourceApiKeyId: string;
			cybersourceApiSecret: string;
			cybersourceApiUrl: string;
			cybersourceCurrency: PaymentCurrency;
			cybersourceCountryIso: PaymentCountryIso;
		};
	}
	interface PlatonCheckConnection {
		provider: PaymentProvider.PLATON;
		configuration: {
			platonClientKey: string;
			platonClientPassword: string;
			platonApiUrl: string;
			platonCurrency: PaymentCurrency;
			platonCountryIso: PaymentCountryIso;
		};
	}
	interface MonobankCheckConnection {
		provider: PaymentProvider.MONOBANK;
		configuration: {
			monobankToken: string;
			monobankApiUrl: string;
			monobankCurrency: PaymentCurrency;
			monobankCountryIso: PaymentCountryIso;
		};
	}
	export type CheckConnectionRequestPayload =
		| AssetCheckConnection
		| MaibCheckConnection
		| DinarCheckConnection
		| BogCheckConnection
		| CybersourceCheckConnection
		| PlatonCheckConnection
		| MonobankCheckConnection;

	type UpdatePaymentSystemParams = {
		configuration:
			| AssetPaymentConfiguration
			| MaibPaymentConfiguration
			| DinarPaymentConfiguration
			| BogPaymentConfiguration
			| CybersourcePaymentConfiguration
			| PlatonPaymentConfiguration
			| MonobankPaymentConfiguration
			| EasyPayPaymentConfiguration
			| OtvPaymentConfiguration;
		name?: string;
		active?: boolean;
	};
}
export enum PaymentCurrency {
	USD = "USD",
	EUR = "EUR",
	UAH = "UAH",
	MDL = "MDL",
	GEL = "GEL",
	AZN = "AZN",
	NULL = "NULL",
}

export enum PaymentCountryIso {
	UKR = "UKR",
	POL = "POL",
	AZE = "AZE",
	MDA = "MDA",
	GEO = "GEO",
	NULL = "NULL",
}

export enum MaibPaymentCurrency {
	USD = "USD",
	EUR = "EUR",
	MDL = "MDL",
}

export enum MaibPaymentCountryIso {
	MDA = "MDA",
}

export enum PaymentProvider {
	ASSETPAYMENT = "assetPayments",
	MAIB = "maib",
	DINARPAY = "dinarPay",
	BOG = "bog",
	CYBERSOURCE = "cybersource",
	PLATON = "platon",
	MONOBANK = "monobank",
	EASYPAY = "easyPay",
	OTV = "otv",
}

export enum WithdrawMethod {
	WITHDRAW = "withdraw",
	WITHDRAW_WITH_HOLD = "withdraw_with_hold",
}
export enum PaymentSystemFrontEndType {
	CUSTOMER = "customer",
	EXECUTOR = "executor",
	TEMPLATE_CUSTOMER = "template_customer",
	TEMPLATE_EXECUTOR = "template_executor",
	TERMINAL_EXECUTOR = "terminal_executor",
}

export enum PaymentRegistrationType {
	FRAME = "frame",
	URL = "url",
}

export enum NoticeDestinationApp {
	EXECUTOR = "EXECUTOR",
	CUSTOMER = "CUSTOMER",
}

export default PaymentSystem;
